// src/auth/AuthGate.jsx
import { useState, useEffect, createContext, useContext } from "react";
import { saveToken, getToken, clearToken, getSession } from "./tokenStore";
import LoginScreen    from "./LoginScreen";
import SignUpScreen   from "./SignUpScreen";
import ForgotPassword from "./ForgotPassword";
import ResetPassword  from "./ResetPassword";
import TermsModal     from "../legal/TermsModal";
import LandingPage    from "../marketing/LandingPage";

// ── Auth context ───────────────────────────────────────────────────────────────
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

// ── Terms acceptance cache — persists in localStorage so modal only shows once ─
const TERMS_KEY = "wc_terms_accepted";

function getLocalTerms(email) {
  try {
    const stored = localStorage.getItem(TERMS_KEY);
    if (!stored) return false;
    const parsed = JSON.parse(stored);
    return parsed[email] === true;
  } catch { return false; }
}

function setLocalTerms(email) {
  try {
    const stored = localStorage.getItem(TERMS_KEY);
    const parsed = stored ? JSON.parse(stored) : {};
    parsed[email] = true;
    localStorage.setItem(TERMS_KEY, JSON.stringify(parsed));
  } catch { /* non-fatal */ }
}

export default function AuthGate({ children }) {
  const [user,        setUser]        = useState(undefined); // undefined = loading
  const [authScreen,  setAuthScreen]  = useState("landing");
  const [showTerms,   setShowTerms]   = useState(false);
  const [termsBusy,   setTermsBusy]   = useState(false);
  const [resetToken,  setResetToken]  = useState(null);

  // ── On mount: check for reset_token param or existing session ─────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const rt = params.get("reset_token");
    if (rt) {
      window.history.replaceState({}, "", window.location.pathname);
      setResetToken(rt);
      setUser(null);
      return;
    }

    if (params.get("payment")) {
      window.history.replaceState({}, "", window.location.pathname);
    }

    const session = getSession();
    if (session) {
      setUser({ id: session.sub, email: session.email });
      checkTerms(session.email);
    } else {
      clearToken();
      setUser(null);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Check terms — local cache first, server only if no local record ────────
  const checkTerms = async (email) => {
    // Already accepted locally → never show again
    if (getLocalTerms(email)) return;

    try {
      const res  = await fetch("/.netlify/functions/wc-auth-terms-check", {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body:    JSON.stringify({ email }),
      });
      const data = await res.json();
      if (data.agreedToTerms) {
        // Server confirms accepted — cache it locally so we don't ask again
        setLocalTerms(email);
      } else {
        setShowTerms(true);
      }
    } catch {
      // Network error — don't block the user, try again next session
      // Only show terms if we have no local record at all
    }
  };

  // ── Called after successful login or signup ────────────────────────────────
  const handleAuthSuccess = ({ token, user: u }) => {
    saveToken(token);
    setUser(u);
    if (u.agreedToTerms) {
      setLocalTerms(u.email); // cache so modal doesn't show on next load
    } else if (!getLocalTerms(u.email)) {
      setShowTerms(true);
    }
  };

  // ── Accept terms ──────────────────────────────────────────────────────────
  const acceptTerms = async () => {
    if (!user) return;
    setTermsBusy(true);
    try {
      await fetch("/.netlify/functions/wc-auth-accept-terms", {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        body:    JSON.stringify({ email: user.email }),
      });
      setLocalTerms(user.email); // cache acceptance immediately
    } catch { /* non-fatal */ }
    setShowTerms(false);
    setTermsBusy(false);
  };

  const signOut = () => {
    clearToken();
    setUser(null);
    setShowTerms(false);
    setAuthScreen("login");
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (user === undefined) {
    return (
      <div style={loadingStyle}>
        <div style={{ fontSize: 32, marginBottom: 12 }}>✍️</div>
        <div style={{ color: "#8b949e", fontSize: 14 }}>Loading…</div>
      </div>
    );
  }

  if (!user) {
    if (resetToken)               return <ResetPassword resetToken={resetToken} onSuccess={handleAuthSuccess} onCancel={() => { setResetToken(null); setAuthScreen("login"); }} />;
    if (authScreen === "landing") return <LandingPage    onGetStarted={() => setAuthScreen("signup")} onLogin={() => setAuthScreen("login")} />;
    if (authScreen === "signup")  return <SignUpScreen   onSwitch={setAuthScreen} onSuccess={handleAuthSuccess} />;
    if (authScreen === "forgot")  return <ForgotPassword onSwitch={setAuthScreen} />;
    return <LoginScreen onSwitch={setAuthScreen} onSuccess={handleAuthSuccess} />;
  }

  return (
    <AuthContext.Provider value={{ user, getToken, signOut }}>
      {showTerms && <TermsModal onAccept={acceptTerms} busy={termsBusy} />}
      <div style={{ visibility: showTerms ? "hidden" : "visible" }}>
        {children}
      </div>
    </AuthContext.Provider>
  );
}

const loadingStyle = {
  minHeight: "100vh", background: "#0d1117",
  display: "flex", flexDirection: "column",
  alignItems: "center", justifyContent: "center",
  fontFamily: "'DM Sans', sans-serif", color: "#e6edf3",
};
